import type { Options } from '@contentful/rich-text-react-renderer';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { BLOCKS } from '@contentful/rich-text-types';
import { readableColor } from 'polished';
import { getImage } from 'gatsby-plugin-image';

import type { Breadcrumb } from '../types';

import SectionContainer from './SectionContainer';
import RichText from './RichTextV2';
import BreadCrumbs from './Breadcrumbs';
import Image from './Image';
import GetAQuoteButtonOrLink from './GetAQuoteRenewalCTA';
import CallToAction from './CallToAction';

const Section = styled.section<{ background: string }>`
  background-color: ${({ background }) => themeGet(`colors.${background}`)};
  color: ${({ theme, background }) => readableColor(
    theme.colors[background],
    theme.colors.black,
    theme.colors.white,
  )};
  padding: 40px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 60px 0;
  }
`;

const Heading = styled.h1`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading1.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading1.fontSize')};
  line-height: ${themeGet('typography.mobile.heading1.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading1.letterSpacing')};
  text-align: center;
  margin: 30px 20px 20px 20px;

  &.heroBannerV2 {
    text-align: left;
    margin-left: 20px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading1.fontSize')};
    line-height: ${themeGet('typography.desktop.heading1.lineHeight')};

    &.heroBannerV2 {
      text-align: center;
      margin-left: 0;
    }
  }
`;

const Wrapper = styled.div<{ version?: string }>`
  display: flex;
  flex-direction: ${(props) => (props.version === 'heroBannerV3' ? 'column' : 'column-reverse')};;
  align-items: center;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    flex-direction: ${(props) => (props.version === 'heroBannerV3' ? 'column' : 'row')};
    align-items: center;
    justify-content: space-between;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 0 28px;
  }
`;

const Content = styled.div<{ version?: string }>`
  height: 100%;
  max-width: ${(props) => (props.version !== 'heroBannerV3' && '425px')};
  text-align: center;
  justify-items: center;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    ${(props) => (props.version !== 'heroBannerV3' && 'max-width: 50%; text-align: left;')}
    margin: 0 15px 0 0;
    justify-items: normal;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin: 0 20px 0 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    ${(props) => (props.version !== 'heroBannerV3' && 'max-width: 520px; min-height: 195px;')}
    padding: 0;
    margin: 0 20px 0 40px;
  }
`;

const BodyCopy = styled.p`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.body2.fontWeight')};
  font-size: ${themeGet('typography.mobile.body2.fontSize')};
  line-height: ${themeGet('typography.mobile.body2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.body2.letterSpacing')};

  &.heroBannerV2{
    text-align: left;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.body1.fontSize')};
    line-height: ${themeGet('typography.desktop.body1.lineHeight')};

    &.heroBannerV2 {
      font-size: ${themeGet('typography.desktop.body2.fontSize')};
      line-height: ${themeGet('typography.desktop.body2.lineHeight')};
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    min-height: 125px;
    &.heroBannerV3 {
      min-height: 0;
    }
  }
`;

const StyledImage = styled(Image)<{ $version?: string; $size?: string; }>`
  width: 100%;
  object-fit: contain;
  max-width: ${(props) => (props.$version !== 'heroBannerV3' && '375px')};
  margin: 0 auto 10px;

  ${({ $size }) => $size === 'large' && css`
    max-width: none;
  `}

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    margin: 0;
    max-width: ${(props) => (props.$version !== 'heroBannerV3' && '560px')};
    border-radius: 12px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    margin: 0 40px 0 0;
  }
`;

const HeroBannerOptions: Options = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <Heading data-testid="heroBannerV1">{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.PARAGRAPH]: (_, children) => <BodyCopy>{children}</BodyCopy>,
  },
};

const HeroBannerOptions2: Options = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <Heading className="heroBannerV2" data-testid="heroBannerV2">{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children) => <Heading className="heroBannerV2">{children}</Heading>,
    [BLOCKS.PARAGRAPH]: (_, children) => <BodyCopy className="heroBannerV2">{children}</BodyCopy>,
  },
};

const HeroBannerOptions3: Options = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <Heading data-testid="heroBannerV3">{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.PARAGRAPH]: (_, children) => <BodyCopy className="heroBannerV3">{children}</BodyCopy>,
  },
};

/**
 * Section that displays the hero banner for the page. Uses the HeroBanner content type
 * @param {Object} data - contentful data obj.
 * @param {String} data.sectionId - id of the section for deep linking
 * @param {String} data.backgroundColor - background color to be used for the component
 * @param {{raw: string}} data.heroTitle - Contentful richtext field for the Hero banner title
 * @param {{raw: string}} data.heroText - Contentful richtext field for the Hero banner body copy
 * @param {Object} data.heroLinks - Button /  LinkWrapper Content type reference for the hero CallToAction
 * @param {Object} data.heroImages - DamAsset Content type reference for the hero banner image
 */
interface HeroBannerProps {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  data: any;
  variant?: string;
  size?: string;
  breadcrumbs?: Breadcrumb[];
  version?: 'heroBannerV1' | 'heroBannerV2' | 'heroBannerV3';
}

const HeroBanner = ({
  data,
  variant,
  size,
  breadcrumbs = [],
  version,
}: HeroBannerProps) => {
  if (!data) return null;
  const {
    sectionId,
    backgroundColor,
    heroTitle,
    heroText,
    heroLinks,
    heroImage,
  } = data;
  const image = getImage(heroImage?.asset?.localImage);

  let contentClass = null;
  if (heroText && version === "heroBannerV2") {
    contentClass = <RichText document={heroText} options={HeroBannerOptions2} />;
  } else if (heroText && version === "heroBannerV3") {
    contentClass = <RichText document={heroText} options={HeroBannerOptions3} />;
  } else if (heroText) {
    contentClass = <RichText document={heroText} options={HeroBannerOptions} />;
  }

  return (
    <Section id={sectionId} background={backgroundColor} data-testid={sectionId}>
      {variant === "with-bread-crumbs" && (
        <BreadCrumbs
          variant={backgroundColor}
          crumbs={breadcrumbs}
        />
      )}
      <SectionContainer fluid>
        {version === 'heroBannerV2'
          ? <RichText document={heroTitle} options={HeroBannerOptions2} />
          : <RichText document={heroTitle} options={HeroBannerOptions} />}
        <Wrapper version={version}>
          {heroText && (
            <Content version={version}>
              {contentClass}
              {heroLinks && (
              <GetAQuoteButtonOrLink
                key={heroLinks.id}
                cta={heroLinks}
                CtaComponent={CallToAction}
                wrapperColor={backgroundColor === "white" ? undefined : "white"}
                inline
                phoneNumberColor={backgroundColor === "white" ? "red" : "white"}
              />
              )}
            </Content>
          )}
          {image && (
            <StyledImage
              $version={version}
              $size={size}
              data={heroImage}
              loading="eager"
              objectFit="contain"
            />
          )}
        </Wrapper>
      </SectionContainer>
    </Section>
  );
};

export default HeroBanner;
